import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';

import connect from 'components/lib/connect';
import CloseReport from 'actions/reporting/close_report';
import NavigateTo from 'actions/current_location/navigate_to';

export const ReportStatus = Object.freeze({
  LOADING: 'LOADING',
  LOADED: 'LOADED',
  RUNNING: 'RUNNING',
  RAN: 'RAN',
  UNMOUNTED: 'UNMOUNTED',
});

function EmbeddedReport({ embeddedReportUrl, onStatusChange, onTimezoneChange, openReport }) {
  const onMessageEvent = e => handleMessageEvent(e, { onStatusChange, onTimezoneChange });

  useEffect(() => {
    onStatusChange(ReportStatus.LOADING);

    if (!embeddedReportUrl) {
      openReport();
    }

    window.addEventListener('message', onMessageEvent);
    return () => {
      window.removeEventListener('message', onMessageEvent);
      onStatusChange(ReportStatus.UNMOUNTED);
    };
  }, []);

  return (
    <iframe
      frameBorder="0"
      height="100%"
      id="lkr-embedded-report"
      scrolling="no"
      src={embeddedReportUrl}
      width="100%"
    />
  );
}

export function handleMessageEvent(event, { onStatusChange, onTimezoneChange }) {
  if (event.origin === 'https://gladly.looker.com' || event.origin === 'https://gladlydev.cloud.looker.com') {
    let data;
    try {
      data = JSON.parse(event.data);
    } catch (e) {
      return;
    }

    if (!data) {
      return;
    }

    switch (data.type) {
      case 'page:changed':
        if (data.page && data.page.type === 'dashboard') {
          if (data.page.absoluteUrl) {
            let absoluteUrl = new URL(data.page.absoluteUrl);
            let absoluteUrlSearchParams = new URLSearchParams(absoluteUrl.search);
            if (absoluteUrlSearchParams.has('query_timezone')) {
              let timezone = absoluteUrlSearchParams.get('query_timezone');
              timezone = timezone === 'user_timezone' || timezone === 'query_saved' ? '' : timezone;
              onTimezoneChange(timezone);
            }
          }
        }
        break;
      case 'dashboard:loaded':
      case 'explore:state:changed':
        onStatusChange(ReportStatus.LOADED);
        break;
      case 'dashboard:run:start':
        onStatusChange(ReportStatus.RUNNING);
        break;
      case 'dashboard:run:complete':
        onStatusChange(ReportStatus.RAN);
        break;
      default:
        return;
    }
  }
}

EmbeddedReport.propTypes = {
  embeddedReportUrl: PropTypes.string,
  onStatusChange: PropTypes.func,
  onTimezoneChange: PropTypes.func,
};

function mapStateToProps({ getProvider }) {
  return {
    currentLocationProvider: getProvider('currentLocation'),
    embeddedReportUrl: _.get(getProvider('embeddedReport').get(), 'expiringUrl.url'),
  };
}

function mapExecuteToProps(executeAction, { onStatusChange }) {
  return {
    onTimezoneChange: (currentLocationProvider, timezone) => {
      const currentLocation = currentLocationProvider.get();
      if (currentLocation.getTimezone() !== timezone) {
        executeAction(NavigateTo, currentLocation.deriveNew({ timezone }));
      }
    },
    onStatusChange: handleStatusChange,
    openReport: currentLocationProvider => executeAction(NavigateTo, currentLocationProvider.get()),
  };

  function handleStatusChange(newStatus) {
    if (newStatus === ReportStatus.UNMOUNTED) {
      executeAction(CloseReport);
    }

    onStatusChange(newStatus);
  }
}

function mergeProps(stateProps, executeProps, ownProps) {
  return {
    ...ownProps,
    ...stateProps,
    ...executeProps,
    onTimezoneChange: timezone => executeProps.onTimezoneChange(stateProps.currentLocationProvider, timezone),
    openReport: () => executeProps.openReport(stateProps.currentLocationProvider),
  };
}

const EmbeddedReportContainer = connect(mapStateToProps, mapExecuteToProps, mergeProps)(EmbeddedReport);

EmbeddedReportContainer.defaultProps = {
  onStatusChange: _.noop,
};

EmbeddedReportContainer.propTypes = {
  onStatusChange: PropTypes.func,
};

export default EmbeddedReportContainer;
